// --- DeliverySlotSelector.jsx ---
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Grid, 
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/SFooter';
import Header from '../components/Header';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from '../i18n';
import { useCart } from '../context/CartContext';
import cartService from '../service/cartService';

// ---------- PURE MATH CONVERSIONS (KEEP THESE) ----------
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;


const formatTime = (date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
};


function istToUtcISO(dateIst) {
  // Take IST clock time and reinterpret it as IST, then convert to real UTC
  const y = dateIst.getFullYear();
  const m = dateIst.getMonth();
  const d = dateIst.getDate();
  const h = dateIst.getHours();
  const min = dateIst.getMinutes();
  const s = dateIst.getSeconds();

  // Build a new Date *as if this time is IST*, then convert to UTC
  return new Date(Date.UTC(y, m, d, h - 5, min - 30, s)).toISOString();
}

function nowInIST() {
  return new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
  );
}



// ---------- FIXED SLOT GENERATOR ----------
const generateDeliverySlots = (status) => {

  // 🔹 STEP 1 — Create "now in IST" WITHOUT browser interference
const now = nowInIST();



  let dayOffset = status !== "AVAILABLE" ? 1 : 0;

  const startHour = 9;
  const endHour = 18;   // 6 PM IST
  const daysToShow = 3;
  const slots = [];

  for (; dayOffset < daysToShow; dayOffset++) {

    const dayDate = new Date(now);
    dayDate.setDate(now.getDate() + dayOffset);

    const daySlots = [];

    // ============ TODAY ============
    if (dayOffset === 0) {

      let earliestPossible = new Date(now.getTime() + 35 * 60 * 1000);
      earliestPossible.setSeconds(0, 0);

      let nineAM = new Date(now);
      nineAM.setHours(9, 0, 0, 0);

      let slotStart = earliestPossible > nineAM ? earliestPossible : nineAM;

      while (slotStart.getHours() < endHour) {

        const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);

        if (slotEnd.getHours() > endHour) break;

  daySlots.push({
  label: `${formatTime(slotStart)} - ${formatTime(slotEnd)}`, // IST label
  time: slotStart,                                           // keep IST Date
  startUTCtime: istToUtcISO(slotStart),                     // ✅ real UTC string
  endUTCtime: istToUtcISO(slotEnd),                         // ✅ real UTC string
  date: dayDate.toDateString(),
});



        slotStart = new Date(slotEnd);
      }
    }

    // ============ TOMORROW & DAY 3 ============
    else {

      let slotStart = new Date(dayDate);
      slotStart.setHours(startHour, 0, 0, 0);

      while (slotStart.getHours() < endHour) {

        const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);

        if (slotEnd.getHours() > endHour) break;

 daySlots.push({
  label: `${formatTime(slotStart)} - ${formatTime(slotEnd)}`, // IST label
  time: slotStart,                                           // keep IST Date
  startUTCtime: istToUtcISO(slotStart),                     // ✅ real UTC string
  endUTCtime: istToUtcISO(slotEnd),                         // ✅ real UTC string
  date: dayDate.toDateString(),
});



        slotStart = new Date(slotEnd);
      }
    }

    if (daySlots.length > 0) {
      slots.push({
        dateLabel:
          dayOffset === 0
            ? "Today"
            : dayOffset === 1
            ? "Tomorrow"
            : dayDate.toLocaleDateString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              }),
        daySlots,
      });
    }
  }

  return slots;
};




const DeliverySlotSelector = ({ onNext }) => {
  const { user } = useAuth();
  const { pincode } = user || {};
  const navigate = useNavigate(); 
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
 const { t } = useTranslation();
    const { cart } = useCart();
  
 
  
  useEffect(() => {
    // simulate loading
    setTimeout(() => {
      
      console.log("slot delivery inv status "+  cart[0].inventoryStatus);
      
      setAvailableSlots(generateDeliverySlots(cart[0].inventoryStatus));
      setIsLoading(false);
    }, 500);
  }, []);


  const handleNextClick = () => {
  if (!selectedSlot) {
    setSnackbarOpen(true);
    return;
  }

  console.log("User saw (IST):", selectedSlot.label);
  console.log("Sending to backend (UTC):",
    selectedSlot.startUTCtime,
    selectedSlot.endUTCtime
  );

  const cartDto = {
    cartId: cart[0].cartId,
    slotStartTime: selectedSlot.startUTCtime, // already UTC ISO
    slotEndTime: selectedSlot.endUTCtime
  };

  cartService.updateSlotSelection(cartDto);
  navigate("/payment");
};

  
  return (
      <>
      <Header />
    <Box sx={{ maxWidth: 800, margin: '40px auto', padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        
        {t(normalizeKey('Select Delivery Slot') ,'Select Delivery Slot')}
      </Typography>

      {/* Display Pincode */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LocationOnIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6">
          {t(normalizeKey('Delivery Pincode') ,'Delivery Pincode')} : {pincode || 'Not available'}
        </Typography>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Slots */}
      {!isLoading && availableSlots.length > 0 && (
        <Box>
          {availableSlots.map((day) => (
            <Box key={day.dateLabel} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {day.dateLabel}
              </Typography>
              <Grid container spacing={2}>
                {day.daySlots.map((slot) => (
                  <Grid item xs={12} sm={6} md={4} key={slot.time}>
                    <Card
                      onClick={() => setSelectedSlot(slot)}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: selectedSlot?.time === slot.time ? 'primary.main' : 'grey.300',
                        backgroundColor: selectedSlot?.time === slot.time ? 'primary.light' : 'white',
                        '&:hover': { boxShadow: 6 },
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 1.5 }}>
                        <AccessTimeIcon color={selectedSlot?.time === slot.time ? 'primary' : 'action'} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {slot.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {day.dateLabel}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}

      {/* Next Button */}
      <Button
        variant="contained"
        color="success"
        size="large"
        fullWidth
        onClick={handleNextClick}
        disabled={!selectedSlot}
        sx={{ mt: 3 }}
      >
        
        {t("next_slot" , "Next")} : 
        {t("confirm_delivery_time" , "Confirm Delivery Time")}

      </Button>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="warning" sx={{ width: '100%' }}>
          Please select a delivery slot to proceed.
        </Alert>
      </Snackbar>
    </Box>
            <Footer />
    </>
  );
};

export default DeliverySlotSelector;
